import { shallowRef, ref, reactive, computed } from 'vue'
import { useTableSort } from '../../../hooks/index'
import { post, getValueByPath, compileData } from '../../../utils/index'
import { useSetStorageComponent } from '../../criteria-form/hook/use-set-storage-component'

export function useTopTable({
  columnUrl,
  columnTableCode,
  column,
  dataUrl,
  dataParam,
  pageSize,
  pageSizes,
  criteriaCode,
  queryData,
  bottomData,
  addItems,
  bottomParentKey,
  preconditionData,
  rowConfig,
  parametersStorageSuffix,
  rowChange,
  search,
  isSelection,
}) {
  const { setParametersBySessionStorage, setPageSizeLocalStorage, getPageSizeLocalStorage } =
    useSetStorageComponent(parametersStorageSuffix)
  const {
    sortings,
    tableSortings,
    sortChange: topSortChange,
    columns,
    getcolumns: topGetColumns,
    reloadColumn,
  } = useTableSort({
    columnUrl,
    columnTableCode,
    column,
    parametersStorageSuffix,
  })

  const topData = shallowRef([])
  const topTableRef = ref(null)
  const topTableLoading = ref(false)
  const topQueryTime = ref(0)
  const page = reactive({
    pageSize: getPageSizeLocalStorage() ?? pageSize,
    pageNo: 1,
  })
  //上表请求参数
  const seachParameters = computed(() => {
    const dataParamCom = compileData(dataParam, { preconditionData: preconditionData.value })
    return {
      criteriaCode,
      tableCode: columnTableCode,
      ...page,
      ...dataParamCom,
      parameters: {
        ...dataParamCom?.parameters,
        ...queryData.value,
      },
      sortings: [
        ...sortings.value,
        ...(dataParam?.sortings ?? []).filter(
          (im) => !sortings.value.some((sor) => sor.field === im.field)
        ),
      ],
    }
  })
  //根据下表数据设置上表当前页复选框状态
  const setCheckbox = () => {
    if (isSelection) {
      if (selectionsMap.get(page.pageNo)?.length > 0) {
        topTableRef.value?.setCheckboxRow?.(selectionsMap.get(page.pageNo), true)
      }
    } else if (bottomParentKey && topData.value.length > 0) {
      topTableRef.value?.setCheckboxRow?.(topData.value, false)
      const checkData = topData.value.filter((top) =>
        bottomData.value.some(
          (bottom) =>
            getValueByPath(bottom, bottomParentKey) === getValueByPath(top, rowConfig.keyField)
        )
      )
      topTableRef.value?.setCheckboxRow?.(checkData, true)
    }
  }
  //获取上表数据
  const getTopData = async (reload) => {
    if (reload) {
      page.pageNo = 1
      clearSelections()
    }
    topTableLoading.value = true
    const startTime = Date.now()
    const [res] = await post(dataUrl, seachParameters.value).catch(() => {
      topTableLoading.value = false
      topQueryTime.value = Date.now() - startTime
    })
    topTableLoading.value = false
    topQueryTime.value = Date.now() - startTime
    if (res) {
      setParametersBySessionStorage(seachParameters.value.parameters)
      topData.value = res.data ?? []
      loadTopTable(res)
    } else {
      topData.value = []
    }
  }
  const loadTopTable = (res) => {
    return topTableRef.value
      ?.loadDataAsync?.((tablePage) => {
        tablePage.pageNo = page.pageNo
        tablePage.pageSize = page.pageSize
        return res
      })
      .then(() => {
        setTimeout(() => {
          setCheckbox()
        })
      })
  }

  const clearTop = () => {
    clearSelections()
    topData.value = []
    page.pageNo = 1
    topTableRef.value?.loadDataAsync?.((tablePage) => {
      tablePage.pageNo = page.pageNo
      tablePage.pageSize = page.pageSize
      return { data: [] }
    })
  }

  const topTableProps = reactive({
    loading: topTableLoading,
    column: columns,
    pageSize,
    pageSizes,
    sortConfig: { defaultSort: tableSortings },
    rowConfig,
    checkboxBefore: async ({ row, checked }) => {
      if (isSelection) {
        return
      }
      let checkDatas = row ? (Array.isArray(row) ? row : [row]) : topData.value
      //全部勾选时获取没有选中的项
      if (checked && bottomParentKey && bottomData.value.length > 0) {
        checkDatas = checkDatas.filter(
          (im) =>
            !bottomData.value.some(
              (bottom) =>
                getValueByPath(bottom, bottomParentKey) === getValueByPath(im, rowConfig.keyField)
            )
        )
      }
      return addItems(checkDatas, checked, setCheckbox)
    },
  })

  //复选框
  const topDataSelections = shallowRef([])
  const selectionsMap = new Map()
  const setSelections = (key, records) => {
    selectionsMap.set(key, records)
    topDataSelections.value = [].concat(...selectionsMap.values())
  }
  const clearSelections = () => {
    if (!isSelection) return
    selectionsMap.clear()
    topDataSelections.value = []
  }
  const checkboxChange = ({ records }) => {
    if (!isSelection) return
    setSelections(page.pageNo, records)
  }
  const checkboxAll = ({ records }) => {
    if (!isSelection) return
    setSelections(page.pageNo, records)
  }
  const topTableEvent = {
    'row-change': (param) => {
      rowChange?.(param)
    },
    'sort-change': ({ sortList }) => {
      topSortChange(sortList)
      search(true)
    },
    'size-change': (size) => {
      page.pageSize = size
      setPageSizeLocalStorage(size)
      getTopData(true)
    },
    'current-change': (pageNo) => {
      page.pageNo = pageNo
      getTopData()
    },
    'checkbox-change': checkboxChange,
    'checkbox-all': checkboxAll,
  }

  return {
    topTableRef,
    topData,
    topTableProps,
    topTableEvent,
    topGetColumns,
    getTopData,
    loadTopTable,
    topQueryTime,
    seachParameters,
    setCheckbox,
    clearTop,
    reloadColumn,
    topDataSelections,
  }
}
